/**
 * Which theme a v1.5 part wears when nobody told it.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────────────────────────
 *
 * Every v1.5 component takes a `theme` prop and maps it onto its folder's `--hv15-*` / `--sv15-*` /
 * `--cv15-*` ramp, because the design system underneath is light-only and a dark-first part cannot
 * read the global tokens directly. That contract is right and it stays.
 *
 * What was wrong was the DEFAULT. Every one of those parts defaulted to `'dark'`, which meant each
 * one re-declared the dark ramp on its own root — so a light ancestor could never reach it. Custom
 * properties inherit, but only until something closer overrides them, and the component's own class
 * is always closer. The result: a preference could repaint the shell and nothing inside it.
 *
 * So the default is no longer a literal. It is whatever the nearest `V15ThemeProvider` says, and the
 * prop is now an OVERRIDE rather than the only way to be themed. Three readings, in order:
 *
 *   1. an explicit `theme` prop            — a story board, or a surface that is always one theme
 *   2. the nearest provider                — the app's appearance preference, set once at the root
 *   3. `'dark'`                            — no provider at all, which is what Storybook's unwrapped
 *                                            stories and any test renderer get
 *
 * Reading 3 is what makes this change safe to land in one pass: a component pulled up on its own,
 * outside any provider, looks exactly as it did before.
 */
import React from 'react';
import type { HeaderTheme } from './Header/theme';

/**
 * `'dark'` and not `undefined`.
 *
 * The fallback has to be a real theme rather than an absence, because a component's CSS reads
 * custom properties that something must have declared — an unthemed root inherits whatever the page
 * happens to carry, which on this app's light-only ground is a dark-first component painted in light
 * tokens. Dark is also what every one of these parts defaulted to before this file existed, so
 * nothing outside a provider changes.
 */
const FALLBACK: HeaderTheme = 'dark';

const V15ThemeContext = React.createContext<HeaderTheme>(FALLBACK);

export interface V15ThemeProviderProps {
  theme: HeaderTheme;
  children: React.ReactNode;
}

/**
 * Supplies the theme to every v1.5 part below it.
 *
 * Mounted once, at the app root, from the appearance preference — see `root.tsx`. It renders no DOM
 * of its own on purpose: the ramp classes are applied by the parts themselves, and a wrapper element
 * here would add a box to every page's layout for nothing.
 *
 * Nesting is meaningful and supported: a surface that must stay one theme regardless of the
 * preference — an artifact preview showing a light deck, say — wraps its own subtree in a second
 * provider rather than passing `theme` down by hand through every level.
 */
export function V15ThemeProvider({ theme, children }: V15ThemeProviderProps) {
  return <V15ThemeContext.Provider value={theme}>{children}</V15ThemeContext.Provider>;
}

/**
 * The theme this part should paint in: its own prop if it was given one, otherwise the provider's.
 *
 * Called by every v1.5 component that used to write `theme = 'dark'` in its destructuring. It is a
 * hook and therefore unconditional, which is exactly why the default could not simply become
 * `theme = useV15Theme()` in the parameter list — a default parameter is only evaluated when the
 * argument is missing, and a hook that runs on some renders and not others is a hook that breaks.
 */
export function useV15Theme(explicit?: HeaderTheme): HeaderTheme {
  const inherited = React.useContext(V15ThemeContext);
  return explicit ?? inherited;
}
